import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import { motion } from "framer-motion";
// import { Link } from "react-router-dom";

// Simple mobile detection hook - DISABLED for desktop mode on mobile
function useIsMobile() {
  // Always return false to force desktop layout
  return false;
}

// --- helpers (same as before) ---
async function loadImageCORS(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.loading = "eager";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function getDominantColor(src, stride = 16) {
  try {
    const img = await loadImageCORS(src);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const w = (canvas.width = img.naturalWidth || img.width);
    const h = (canvas.height = img.naturalHeight || img.height);
    if (!w || !h) return { r: 240, g: 240, b: 240 };
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const Rs = [], Gs = [], Bs = [];
    for (let y = 0; y < h; y += stride) {
      for (let x = 0; x < w; x += stride) {
        const i = (y * w + x) * 4;
        const A = data[i + 3];
        if (A < 200) continue;
        Rs.push(data[i]); Gs.push(data[i + 1]); Bs.push(data[i + 2]);
      }
    }
    if (!Rs.length) return { r: 240, g: 240, b: 240 };
    const median = (arr) => {
      arr.sort((a, b) => a - b);
      const m = Math.floor((arr.length - 1) / 2);
      return arr.length % 2 ? arr[m] : Math.round((arr[m] + arr[m + 1]) / 2);
    };
    return { r: median(Rs), g: median(Gs), b: median(Bs) };
  } catch {
    return { r: 240, g: 240, b: 240 };
  }
}

function rgbToCss({ r, g, b }) { return `rgb(${r}, ${g}, ${b})`; }

function chooseTextColor(bg) {
  const toLin = (c) => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * toLin(bg.r) + 0.7152 * toLin(bg.g) + 0.0722 * toLin(bg.b);
  const contrastWhite = 1.05 / (L + 0.05);
  const contrastBlack = (L + 0.05) / 0.05;
  return contrastWhite >= contrastBlack ? "white" : "black";
}

const slides = [
  { id: 1, src: "./images/i/00.jpg", alt: "Elegant lamp" },
  { id: 2, src: "./images/i/2.jpg", alt: "Another view" },
  { id: 3, src: "./images/i/3.jpg", alt: "Detail shot" },
    // { id: 4, src: "./images/i/4.jpg", alt: "Modern interior" }, 
    { id: 5, src: "./images/i/5.jpg", alt: "Cozy living room" },
    { id: 6, src: "./images/i/6.jpg", alt: "Stylish workspace" },
        { id: 7, src: "./images/i/1.jpg", alt: "Stylish workspace" },
];

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export default function HomePage() {
  const randomized = React.useMemo(() => shuffle(slides), []);
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Only animate text the first time
  const hasAnimatedTextRef = React.useRef(false);

  const handleSlideChange = (sw) => {
    const idx = typeof sw.realIndex === "number" ? sw.realIndex : sw.activeIndex || 0;
    setActiveIndex(idx);
  };

  // Fixed text colors for the static background
  const textBase = "text-neutral-900";
  const textMuted = "text-neutral-700";

//   const buttonClasses = isDarkText ? "bg-neutral-900 text-white" : "bg-white text-neutral-900";


  // Decide animation props for text: run only on first render
  const textInitial = hasAnimatedTextRef.current ? false : { opacity: 0, y: 300 };
  const textAnimate = { opacity: 1, y: 0 };
  const textTransition = { duration: 3, ease: [0, 1, 0, 1] };

  // After first paint, mark as animated to avoid future transitions
  React.useEffect(() => {
    if (!hasAnimatedTextRef.current) {
      // Delay to ensure first animation has started
      const t = setTimeout(() => { hasAnimatedTextRef.current = true; }, 0);
      return () => clearTimeout(t);
    }
  }, []);

  // Detect mobile
  const isMobile = useIsMobile();

  // Animation props for mobile (on-scroll) vs desktop (on-mount)
  const getMotionProps = (delay = 0) =>
    isMobile
      ? {
          initial: { opacity: 0, y: 100 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.3 },
          transition: { duration: 1.2, ease: [0, 1, 0, 1], delay },
        }
      : {
          initial: textInitial,
          animate: textAnimate,
          transition: { ...textTransition, delay },
        };

  return (
    <main className="w-full pt-[65px]">
      {/*
        On mobile: grid-rows-2, grid-cols-1 (left on top, right below)
        On md+: grid-cols-2, grid-rows-1 (side by side)
      */}
      <section className="grid  md:grid-rows-1 grid-cols-1 md:grid-cols-2" style={{ height: '550px' }}>
        {/* Left: Swiper with 3s autoplay */}
        <div className="relative w-full h-full row-start-1 col-start-1">
  <Swiper
    modules={[Autoplay, EffectFade]}
    effect="fade"
    autoplay={{ delay: 5000, disableOnInteraction: false }}
    loop
    className="h-full"
    onSlideChange={handleSlideChange}
    fadeEffect={{ crossFade: true }}
  >
    {randomized.map((s, i) => (
      <SwiperSlide key={s.id}>
        {/* Wrap in relative/overflow-hidden to mask zoom edges */}
        <div className="relative h-full w-full overflow-hidden">
          <motion.img
            src={s.src}
            alt={s.alt}
            className="h-full w-full object-cover will-change-transform"
            // Restart zoom when slide becomes active
            initial={{ scale: 1 }}
            animate={{
              // Active slide zooms from 1 -> 1.1, inactive stays at 1
              scale: activeIndex === i ? 1.1 : 1,
            }}
            transition={{
              // Match autoplay delay for a gentle zoom
              duration: 15,
              ease: [0.2, 0.0, 0.2, 1],
            }}
          />
        </div>
      </SwiperSlide>
    ))}
  </Swiper>
        </div>

    {/* Right: static background, below on mobile, right on desktop */}
  <div className="relative w-full h-full overflow-hidden row-start-2 md:row-start-1 col-start-1 md:col-start-2 bg-canvas-300">
          <div className={`relative z-10 h-full w-full ${textBase}`}>
            <div className="h-full max-w-xl mx-auto px-8 md:px-12 flex flex-col justify-center gap-6">
              {/* Headline: animate once */}
              <motion.p
                {...getMotionProps(0)}
                className="text-5xl md:text-7xl font-serif leading-tight"
              >
                <img src="./images/logo_name.png" alt="Manar logo" className="h-[5rem] w-auto select-none" style={{ filter: "invert(1) brightness(0.1) contrast(1.1)" }} />
              </motion.p>

              {/* Price: animate once */}
              <motion.h3
                {...getMotionProps(0.1)}
                className={`lob text-b text-2xl md:text-3xl uppercase`}
              >
                DESIGN AND MANAGEMENT
              </motion.h3>

              {/* Description: animate once */}
              <motion.p
                {...getMotionProps(0.2)}
                className={`${textMuted}`}
              >
                At MANAR, Through architecture and interiors, we bring together design, function, and emotion — creating environments that inspire ,we believe every space has a story. 
              </motion.p>
              <motion.div
                {...getMotionProps(0.3)}
                className="flex items-center gap-4"
              >
                {/* <Link
                  to="/management"
                  className={[
                    "relative inline-block rounded-full px-6 py-3",
                    // base gradient positioned at left, oversized so we can slide it
                    "bg-gradient-to-r from-neutral-900 via-neutral-700 to-neutral-900",
                    "text-white",
                    "bg-[length:200%_100%]",           // or use bg-200-100 from config
                    "bg-[position:0%_50%]",  
                    "bg-[linear-gradient(90deg, #111 0%, #444 50%, #111 100%)]",            // or use bg-left-center from config
                    "transition-[background-position,color] duration-500 ease-out",
                    "hover:bg-[position:100%_50%]",    // or hover:bg-right-center
                  ].join(" ")}
                >
                  DESIGN AND MANAGEMENT
                </Link> */}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
